import { DOCUMENT } from '@angular/common';
import { DestroyRef, EventEmitter, Inject, Injectable } from '@angular/core';
import { Direction } from '@angular/cdk/bidi';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, distinctUntilChanged, firstValueFrom, forkJoin, Subscription, timeout } from 'rxjs';
import { backendLanguages, fallbackLanguages, getSavedApiLanguage, LANGUAGE_STORAGE_KEY, LanguageOption, toApiLanguage } from '../i18n/languages';
import { LanguageClient } from './deliordering-frontend';
import { StorageService } from './storage.service';

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private availableLanguages = fallbackLanguages();
  private readonly selectedCode = new BehaviorSubject(getSavedApiLanguage());
  readonly languageCode$ = this.selectedCode.asObservable().pipe(distinctUntilChanged());
  private languageRequest?: Subscription;
  private loadedStoreId: number | null = null;
  private loadingStoreId: number | null = null;
  private catalogVersion = 0;
  private selectionVersion = 0;
  private selectionRequest?: Subscription;

  constructor(
    private readonly translate: TranslateService,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly destroyRef: DestroyRef,
    private readonly translationClient: LanguageClient,
    private readonly storage: StorageService
  ) {
    this.translate.onLangChange.pipe(takeUntilDestroyed(destroyRef)).subscribe(() => this.syncDocument());
  }

  get languages(): readonly LanguageOption[] {
    return this.availableLanguages;
  }

  get code(): string {
    return this.selectedCode.value;
  }

  get direction(): Direction {
    return ['ar', 'fa', 'ur'].includes(this.code.split('-')[0].toLowerCase()) ? 'rtl' : 'ltr';
  }

  async initialize(): Promise<void> {
    await firstValueFrom(forkJoin([
      this.translate.use(this.code),
      this.translate.setFallbackLang('en')
    ]));
    this.syncDocument();
    // A slow or unavailable catalog must not prevent the kiosk from opening.
    this.loadLanguages();
  }

  select(code: string): void {
    const requested = toApiLanguage(code)?.toLowerCase();
    const language = this.availableLanguages.find(option => option.code.toLowerCase() === requested)?.code ?? 'en';
    const version = ++this.selectionVersion;
    this.selectionRequest?.unsubscribe();
    this.selectionRequest = this.translate.use(language).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (version !== this.selectionVersion) return;
      try {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
      } catch {
        // Switching still works when browser storage is unavailable.
      }
      this.selectedCode.next(language);
      this.syncDocument();
    });
  }

  loadLanguages(force = false): void {
    const storeId = this.storage.getNumericStoreId();
    if (storeId !== null && (this.loadingStoreId === storeId || (!force && this.loadedStoreId === storeId))) return;
    this.languageRequest?.unsubscribe();
    const version = ++this.catalogVersion;
    this.loadingStoreId = null;
    this.loadedStoreId = null;
    this.availableLanguages = fallbackLanguages();
    if (storeId === null) {
      this.select(this.code);
      return;
    }
    this.loadingStoreId = storeId;
    this.languageRequest = this.translationClient.getLanguages(storeId).pipe(
      timeout(8000),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: response => {
        if (version !== this.catalogVersion) return;
        this.availableLanguages = backendLanguages(response.result ?? []);
        this.loadedStoreId = storeId;
        this.loadingStoreId = null;
        this.select(this.code);
      },
      error: error => {
        if (version !== this.catalogVersion) return;
        console.warn('Language catalog unavailable; keeping the default English choice.', error);
        this.loadingStoreId = null;
        this.availableLanguages = fallbackLanguages();
        this.select(this.code);
      }
    });
  }

  t(source: string, params?: Record<string, unknown>): string {
    if (!source) return '';
    return this.translate.instant(source, params) as string;
  }

  private syncDocument(): void {
    this.document.documentElement.lang = this.code;
    this.document.documentElement.dir = this.direction;
  }
}

// Material dialogs and overlays must follow a language change as well as the page.
@Injectable()
export class UiDirectionality {
  readonly change = new EventEmitter<Direction>();

  constructor(private readonly language: LanguageService, destroyRef: DestroyRef) {
    language.languageCode$.pipe(takeUntilDestroyed(destroyRef)).subscribe(() => this.change.emit(this.value));
    destroyRef.onDestroy(() => this.change.complete());
  }

  get value(): Direction {
    return this.language.direction;
  }
}
